import Image from "next/image";
import { ArrowRight } from "lucide-react";
import type { CSSProperties } from "react";

import {
  CcPhotoScene,
  CcToolLogo,
} from "@/components/services/shared/CcPhotoScene";
import type { Meter } from "@/components/services/shared/demo-parts";

import type { AiValueDemoCopy } from "./ai-value-demo-copy";

/**
 * The three "Värdet av AI-synlighet" tabs as real photos with cards for the
 * made-up solar installer laid over them: named in AI answers, the right
 * sources, more enquiries. Copy comes from `ai-value-demo.json`.
 */

const delay = (d: number) => ({ "--d": d }) as CSSProperties;

/** Logo (or letter badge) in front of a meter row. */
function MeterMark({ meter }: { meter: Meter }) {
  if (meter.logo) {
    return <Image src={meter.logo} alt="" width={16} height={16} unoptimized />;
  }
  return <i className="cc-av-badge">{meter.badge}</i>;
}

function MeterList({ meters }: { meters: Meter[] }) {
  return (
    <ul className="cc-av-meters">
      {meters.map((meter, index) => (
        <li key={meter.label} style={{ "--i": index } as CSSProperties}>
          <MeterMark meter={meter} />
          <span>{meter.label}</span>
          <i
            className="cc-av-meters__bar"
            style={{ "--w": `${meter.share * 100}%` } as CSSProperties}
          />
          <b>{meter.valueText}</b>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------- 1. named in answers

export function AiAnswersPhotoView({
  copy,
}: {
  copy: AiValueDemoCopy["answers"];
}) {
  const max = Math.max(...copy.mentionsSeries);
  return (
    <CcPhotoScene photo={copy.photo} variant="ai-answers">
      <div className="cc-wv-card cc-av-mentions" style={delay(0)}>
        <p className="cc-wv-card__head">{copy.mentionsTitle}</p>
        <p className="cc-av-mentions__value">
          <b>{copy.mentionsValue}</b>
          <span>{copy.mentionsPeriod}</span>
        </p>
        <ul className="cc-sv-visits__bars">
          {copy.mentionsSeries.map((value, index) => (
            <li
              key={index}
              style={
                {
                  "--h": `${(value / max) * 100}%`,
                  "--i": index,
                } as CSSProperties
              }
            />
          ))}
        </ul>
        <small className="cc-av-mentions__change">{copy.mentionsChange}</small>
      </div>

      <div className="cc-wv-card cc-av-assistants" style={delay(1)}>
        <p className="cc-wv-card__head">
          {copy.assistantsTitle}
          <em>{copy.assistantsMeta}</em>
        </p>
        <MeterList meters={copy.assistants.slice(0, 4)} />
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 2. the right sources

export function AiSourcesPhotoView({
  copy,
}: {
  copy: AiValueDemoCopy["sources"];
}) {
  return (
    <CcPhotoScene photo={copy.photo} variant="ai-sources">
      <div className="cc-wv-card cc-av-sources" style={delay(0)}>
        <p className="cc-wv-card__head">{copy.sourcesTitle}</p>
        <MeterList meters={copy.sources.slice(0, 4)} />
      </div>

      <div className="cc-wv-card cc-av-facts" style={delay(1)}>
        <p className="cc-wv-card__head">
          {copy.factsTitle}
          <em>{copy.factsMeta}</em>
        </p>
        <ul>
          {copy.facts.slice(0, 3).map((fact) => (
            <li key={fact.label}>
              <span>
                <small>{fact.label}</small>
                {fact.value}
              </span>
              <em data-tone={fact.tone}>{fact.status}</em>
            </li>
          ))}
        </ul>
      </div>
    </CcPhotoScene>
  );
}

// ---------------------------------------------------------------- 3. more get in touch

export function AiLeadsPhotoView({ copy }: { copy: AiValueDemoCopy["leads"] }) {
  const enquiry = copy.rows[0];
  return (
    <CcPhotoScene photo={copy.photo} variant="ai-leads">
      <div className="cc-wv-card cc-wv-mail cc-av-mail" style={delay(0)}>
        <p className="cc-wv-card__head">
          <CcToolLogo name="gmail" />
          {copy.mailApp}
          <em>{copy.mailTime}</em>
        </p>
        <b>{copy.mailTitle}</b>
        <span className="cc-av-mail__from">
          {enquiry.photo ? (
            <Image src={enquiry.photo} alt="" width={28} height={28} />
          ) : null}
          <small>{enquiry.name}</small>
        </span>
        <p>{enquiry.subject}</p>
        <span className="cc-wv-mail__source">
          <CcToolLogo name="chatgpt" size={12} />
          {copy.source}
        </span>
      </div>

      <div className="cc-wv-card cc-av-rate" style={delay(1)}>
        <p className="cc-wv-card__head">{copy.rateTitle}</p>
        <div className="cc-av-rate__row">
          <span>
            <small>{copy.rateBeforeLabel}</small>
            <b>{copy.rateBefore}</b>
          </span>
          <ArrowRight size={16} strokeWidth={2} />
          <span data-strong="true">
            <small>{copy.rateAfterLabel}</small>
            <b>{copy.rateAfter}</b>
          </span>
        </div>
      </div>
    </CcPhotoScene>
  );
}
